import { Link, useLocation, useNavigate } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { CONTACT, IMAGES } from "../../data/content";
import { useSite } from "../../context/SiteContext";

const Footer = () => {
  const { scrollToId } = useSite();
  const location = useLocation();
  const navigate = useNavigate();

  const go = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  return (
    <footer className="bg-[#05081C] text-white py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr] gap-12">
          <div>
            <span className="flex items-center gap-3">
              <img src={IMAGES.logoMark} alt="" loading="lazy" decoding="async" className="h-11 w-auto brightness-0 invert" />
              <img src={IMAGES.logoWordmark} alt="Medlyn 360" loading="lazy" decoding="async" className="h-9 w-auto brightness-0 invert" />
            </span>
            <p className="mt-5 text-sm leading-relaxed text-white/50 max-w-xs">
              Virtual Live Human Medical Scribing for US physicians. Expert trained scribes. Live documentation. No AI-generated notes.
            </p>
            <a
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="Medlyn 360 on LinkedIn"
              className="mt-5 inline-flex p-2.5 rounded-full border border-white/15 text-white/60 hover:text-brand-sky hover:border-brand-sky transition-colors duration-300"
            >
              <Linkedin size={16} />
            </a>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-5">Services</p>
            <ul className="space-y-3">
              <li>
                <button data-testid="footer-service-medical" onClick={() => go("services")} className="text-left text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">
                  Virtual Medical Scribing
                </button>
              </li>
              <li>
                <Link to="/dental" data-testid="footer-service-dental" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">
                  Dental Virtual Front Desk
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-5">Company</p>
            <ul className="space-y-3">
              {[
                { label: "About Us", id: "founders" },
                { label: "How It Works", id: "how-it-works" },
                { label: "ROI Calculator", id: "roi" },
                { label: "FAQ", id: "faq" },
                { label: "Contact", id: "contact" },
              ].map((l) => (
                <li key={l.id}>
                  <button data-testid={`footer-nav-${l.id}`} onClick={() => go(l.id)} className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-5">Resources</p>
            <ul className="space-y-3">
              <li><Link to="/resources" data-testid="footer-resources-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">Resources</Link></li>
              <li><Link to="/resources" data-testid="footer-blog-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">Blog</Link></li>
              <li><a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">{CONTACT.email}</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-5">Legal</p>
            <ul className="space-y-3">
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">Privacy Policy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">Terms of Use</Link></li>
              <li><Link to="/hipaa" data-testid="footer-hipaa-link" className="text-sm text-white/60 hover:text-brand-sky transition-colors duration-300">HIPAA Statement</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Medlyn 360. All rights reserved.</p>
          <p className="text-xs text-white/40">Your clinical documentation partner.</p>
        </div>
      </div>
    </footer>
  );
};
export default Footer;
